/**
 * CheckInFormBuilder - Clinic Manager
 * 
 * Manages clinic-level settings:
 * - Clinic selector and basic info (name, description)
 * - QR code generation and management
 * - Clinic specialties and CareMap integration
 * - Operating hours and scheduling
 * - Provider approval settings
 * - Registrar configuration and assignment
 * 
 * PET (Patient Encounter Template) editing—form fields, welcome message, intake conversation—
 * has been moved to PETManagementModal on the LLM Query Registry page.
 */

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
  Menu,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  Checkbox,
  ListSubheader,
} from '@mui/material';
import {
  Add,
  Delete,
  Star,
  MoreVert,
  ContentCopy,
  Save,
  ExpandMore,
  Warning,
  QrCode2,
  Link,
  Check,
  Map as MapIcon,
  Business,
  LocalHospital,
  CheckBoxOutlineBlank,
  CheckBox as CheckBoxIcon,
  AccessTime,
  EventBusy,
  Schedule,
  AllInclusive,
  PersonAdd,
  Badge,
  CreditCard,
} from '@mui/icons-material';
import { AuthContext } from '@healthdesk/shared-hooks';
import {
  getCheckInTemplates,
  createCheckInTemplate,
  updateCheckInTemplate,
  deleteCheckInTemplate,
  setDefaultTemplate,
  // Clinic metadata & CareMap integration
  getClinicMetadata,
  updateClinicMetadata,
  // Department management
  getDepartments,
  // Specialty constants
  AAMC_SPECIALTIES,
  COMMON_CLINIC_SPECIALTIES,
  getSpecialtyLabel,
  db,
} from '@healthdesk/shared-services';
import { QRCodeSVG } from 'qrcode.react';
import ProviderApprovalSettings from './ProviderApprovalSettings';
import RegistrarConfigCard from './RegistrarConfigCard';
import RegistrarAssignmentCard from './RegistrarAssignmentCard';
import FieldHighlighter from '../../../patient/components/individual/medications/FieldHighlighter';
import { chartmindAdminService } from '../../../../services/chartmind';
import ConversationTemplatePicker from '../conversationTemplates/ConversationTemplatePicker';

// Default welcome message for new templates (used when creating a new template)
const DEFAULT_WELCOME_MESSAGE = `Welcome to our clinic!

Please review and confirm your information below. This helps us provide you with the best possible care.

If any information is incorrect, please update it before submitting.`;

/**
 * Main CheckInFormBuilder component
 */
const CheckInFormBuilder = () => {
  const { user, userData } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Current template editing state
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  
  // UI state
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState(null);
  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
  
  // QR code state - derived from Encounter Template inviteCode
  const [joinUrl, setJoinUrl] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Clinic metadata state (for CareMap integration)
  // Note: clinicName is now unified with templateName (single source of truth when a template is selected)
  const [clinicDisplayName, setClinicDisplayName] = useState(''); // From metadata when no template selected
  const [clinicSpecialties, setClinicSpecialties] = useState([]);
  const [displayOnCareMap, setDisplayOnCareMap] = useState(false);
  const [clinicMetadataLoading, setClinicMetadataLoading] = useState(false);
  
  // Operating hours state
  const [is24x7, setIs24x7] = useState(false);
  const [byAppointmentOnly, setByAppointmentOnly] = useState(false);
  const [weeklyHours, setWeeklyHours] = useState({
    monday: { isOpen: true, openTime: '09:00', closeTime: '17:00', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
    tuesday: { isOpen: true, openTime: '09:00', closeTime: '17:00', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
    wednesday: { isOpen: true, openTime: '09:00', closeTime: '17:00', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
    thursday: { isOpen: true, openTime: '09:00', closeTime: '17:00', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
    friday: { isOpen: true, openTime: '09:00', closeTime: '17:00', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
    saturday: { isOpen: false, openTime: '09:00', closeTime: '13:00', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
    sunday: { isOpen: false, openTime: '', closeTime: '', breakStart: '', breakEnd: '', morningCapacity: 10, afternoonCapacity: 10 },
  });
  const [closureExceptions, setClosureExceptions] = useState([]); // Array of { date: 'YYYY-MM-DD', reason: string }
  const [newClosureDate, setNewClosureDate] = useState('');
  const [newClosureReason, setNewClosureReason] = useState('');
  
  // Department state
  const [departments, setDepartments] = useState([]);
  const [, setDepartmentsLoading] = useState(true);
  const [selectedDepartmentForNew, setSelectedDepartmentForNew] = useState('');
  
  // Patient identification requirements (stored on check-in template, controlled by facility role)
  const [requirePhotoID, setRequirePhotoID] = useState(false);
  const [requireInsuranceCard, setRequireInsuranceCard] = useState(false);
  const [conversationTemplateRef, setConversationTemplateRef] = useState(null);

  // Fee configuration (registration/check-in fee) — facility and chartmind-supervisor
  const [feeConfig, setFeeConfig] = useState({
    registration: {
      amountKsh: 0,
      currency: 'KES',
      enabled: false,
      label: 'Registration fee',
      provider: 'paystack',
    },
  });
  const [messagingConfig, setMessagingConfig] = useState({
    replyEmailDomain: '',
    maxRecipientsPerBlast: '500',
  });
  const [messagingConfigLoading, setMessagingConfigLoading] = useState(false);
  const [messagingConfigSaving, setMessagingConfigSaving] = useState(false);
  const [messagingConfigError, setMessagingConfigError] = useState('');
  const [messagingConfigSuccess, setMessagingConfigSuccess] = useState('');

  // (Custom field form state and conversation template state have been moved to PETManagementModal)

  // Highlight the Clinic dropdown label every 7s when no clinic is selected (LTR then RTL wipe)
  const [highlightClinicLabel, setHighlightClinicLabel] = useState(false);
  const newTemplateNameRef = useRef(null);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);
  const HIGHLIGHT_ANIMATION_MS = 1000; // LTR 500ms + RTL 500ms
  const HIGHLIGHT_REPEAT_MS = 7000;
  useEffect(() => {
    if (templates.length === 0 || selectedTemplateId) {
      setHighlightClinicLabel(false);
      return;
    }
    const runHighlight = () => {
      if (!isMountedRef.current) return;
      setHighlightClinicLabel(true);
      const off = setTimeout(() => {
        if (isMountedRef.current) setHighlightClinicLabel(false);
      }, HIGHLIGHT_ANIMATION_MS);
      return () => clearTimeout(off);
    };
    runHighlight(); // run once when dropdown appears with no selection
    const interval = setInterval(runHighlight, HIGHLIGHT_REPEAT_MS);
    return () => {
      clearInterval(interval);
      setHighlightClinicLabel(false);
    };
  }, [templates.length, selectedTemplateId]);

  // Get clinic ID from user data
  const clinicId = userData?.facilityId || userData?.location || userData?.clinicId;
  // (QR code is derived from the Encounter Template invite code - no separate generation needed)
  
  // Handle browser close/refresh with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const loadTemplates = useCallback(async () => {
    if (!clinicId) return;

    setLoading(true);
    setError(null);
    try {
      const fetchedTemplates = await getCheckInTemplates(clinicId);
      setTemplates(fetchedTemplates);
      // Don't auto-select: leave dropdown empty so user picks a clinic (placeholder shows "Select Clinic Here").
    } catch (err) {
      console.error('[CheckInFormBuilder] Error loading templates:', err);
      setError('Failed to load clinics');
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  const loadDepartments = useCallback(async () => {
    if (!clinicId) return;

    setDepartmentsLoading(true);
    try {
      const fetchedDepartments = await getDepartments(clinicId);
      setDepartments(fetchedDepartments);

      // Set default department for new clinic dialog
      if (fetchedDepartments.length > 0) {
        const generalDept = fetchedDepartments.find(d => d.name === 'General');
        setSelectedDepartmentForNew(generalDept?.id || fetchedDepartments[0].id);
      }
    } catch (err) {
      console.error('[CheckInFormBuilder] Error loading departments:', err);
      // Don't set error - departments are optional
    } finally {
      setDepartmentsLoading(false);
    }
  }, [clinicId]);

  const loadClinicMetadata = useCallback(async () => {
    if (!clinicId) return;

    setClinicMetadataLoading(true);
    try {
      const metadata = await getClinicMetadata(clinicId);
      if (metadata) {
        setClinicDisplayName(metadata.clinicName || '');
        // Note: clinicName is now unified with templateName (loaded via selectTemplate)
        setClinicSpecialties(metadata.specialties || []);
        setDisplayOnCareMap(metadata.displayOnCareMap || false);

        // Load operating hours
        setIs24x7(metadata.is24x7 || false);
        setByAppointmentOnly(metadata.byAppointmentOnly || false);
        if (metadata.weeklyHours) {
          setWeeklyHours(metadata.weeklyHours);
        }
        setClosureExceptions(metadata.closureExceptions || []);
        if (metadata.feeConfig && typeof metadata.feeConfig === 'object') {
          setFeeConfig(prev => ({
            ...prev,
            ...metadata.feeConfig,
            registration: {
              amountKsh: metadata.feeConfig.registration?.amountKsh ?? 0,
              currency: metadata.feeConfig.registration?.currency || 'KES',
              enabled: !!metadata.feeConfig.registration?.enabled,
              label: metadata.feeConfig.registration?.label || 'Registration fee',
              provider: metadata.feeConfig.registration?.provider === 'stripe' ? 'stripe' : 'paystack',
            },
          }));
        }
      }
    } catch (err) {
      console.error('[CheckInFormBuilder] Error loading clinic metadata:', err);
    } finally {
      setClinicMetadataLoading(false);
    }
  }, [clinicId]);

  // Load templates and departments
  useEffect(() => {
    if (clinicId) {
      loadTemplates();
      loadDepartments();
    } else {
      setError('Facility ID not found. Please ensure your account is associated with a facility.');
      setLoading(false);
      setDepartmentsLoading(false);
    }
  }, [clinicId, loadTemplates, loadDepartments]);

  // Derive the join URL from the check-in template's inviteCode (or from the PET when not stored on check-in template)
  useEffect(() => {
    if (!selectedTemplateId) {
      setJoinUrl(null);
      return;
    }
    const checkInTemplate = templates.find(t => t.id === selectedTemplateId);
    const baseUrl = window.location.origin;

    // Prefer inviteCode stored on the check-in template so facility role can show QR/link without reading chartmindAdmins
    if (checkInTemplate?.inviteCode) {
      setJoinUrl(`${baseUrl}/join/t_${checkInTemplate.inviteCode}`);
      return;
    }

    const ref = checkInTemplate?.encounterTemplateRef;
    if (!ref?.adminId || !ref?.templateId) {
      setJoinUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const msiTemplate = await chartmindAdminService.getTemplate(ref.adminId, ref.templateId);
        if (cancelled) return;
        if (msiTemplate?.inviteCode) {
          setJoinUrl(`${baseUrl}/join/t_${msiTemplate.inviteCode}`);
        } else {
          setJoinUrl(null);
        }
      } catch (err) {
        console.error('[CheckInFormBuilder] Error resolving invite code:', err);
        if (!cancelled) setJoinUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTemplateId, templates]);

  // Load clinic metadata on mount
  useEffect(() => {
    if (clinicId) {
      loadClinicMetadata();
    }
  }, [clinicId, loadClinicMetadata]);

  const loadMessagingConfig = useCallback(async () => {
    if (!clinicId) return;
    setMessagingConfigLoading(true);
    try {
      const configSnap = await db
        .collection('facilities')
        .doc(clinicId)
        .collection('settings')
        .doc('messagingConfig')
        .get();
      const data = configSnap.exists ? configSnap.data() : {};
      setMessagingConfig({
        replyEmailDomain: data?.replyEmailDomain || '',
        maxRecipientsPerBlast: String(data?.maxRecipientsPerBlast || 500),
      });
    } catch (err) {
      console.warn('[CheckInFormBuilder] Error loading messaging config:', err);
      setMessagingConfigError('Failed to load messaging settings');
    } finally {
      setMessagingConfigLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    if (clinicId) {
      loadMessagingConfig();
    }
  }, [clinicId, loadMessagingConfig]);

  // Save clinic metadata (called when saving template or when saving hours only)
  const saveClinicMetadata = async () => {
    if (!clinicId) return;
    const clinicNameToSave = templateName || clinicDisplayName;
    try {
      await updateClinicMetadata(clinicId, {
        clinicName: clinicNameToSave,
        specialties: clinicSpecialties,
        displayOnCareMap: displayOnCareMap,
        checkInUrl: joinUrl || null,
        locationId: userData?.location || null, // Link to CareMap location
        // Operating hours
        is24x7: is24x7,
        byAppointmentOnly: byAppointmentOnly,
        weeklyHours: weeklyHours,
        closureExceptions: closureExceptions,
        feeConfig: feeConfig,
      });
    } catch (err) {
      console.error('[CheckInFormBuilder] Error saving clinic metadata:', err);
      throw err;
    }
  };

  const handleSaveClinicSettingsOnly = async () => {
    if (!selectedTemplateId) return;
    setSaving(true);
    setError(null);
    try {
      await saveClinicMetadata();
      setSuccess('Clinic settings saved');
      setHasChanges(false);
      await loadClinicMetadata();
      setTimeout(() => { if (isMountedRef.current) setSuccess(null); }, 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save clinic settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMessagingConfig = async () => {
    if (!clinicId) return;
    setMessagingConfigSaving(true);
    setMessagingConfigError('');
    setMessagingConfigSuccess('');
    const parsedMax = Number(messagingConfig.maxRecipientsPerBlast);
    if (!Number.isFinite(parsedMax) || parsedMax <= 0) {
      setMessagingConfigSaving(false);
      setMessagingConfigError('Max recipients per message must be greater than 0');
      return;
    }

    try {
      await db
        .collection('facilities')
        .doc(clinicId)
        .collection('settings')
        .doc('messagingConfig')
        .set(
          {
            replyEmailDomain: String(messagingConfig.replyEmailDomain || '').trim(),
            maxRecipientsPerBlast: Math.floor(parsedMax),
            updatedAt: new Date(),
            updatedBy: user?.uid || null,
          },
          { merge: true }
        );
      setMessagingConfigSuccess('Messaging settings saved');
      setTimeout(() => { if (isMountedRef.current) setMessagingConfigSuccess(''); }, 3000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error saving messaging config:', err);
      setMessagingConfigError(err?.message || 'Failed to save messaging settings');
    } finally {
      setMessagingConfigSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!joinUrl) return;
    
    try {
      await navigator.clipboard.writeText(joinUrl);
      setLinkCopied(true);
      setTimeout(() => { if (isMountedRef.current) setLinkCopied(false); }, 2000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error copying link:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplateId(template.id);
    setTemplateName(template.name || '');
    setTemplateDescription(template.description || '');
    setRequirePhotoID(template.requirePhotoID || false);
    setRequireInsuranceCard(template.requireInsuranceCard || false);
    setConversationTemplateRef(template.conversationTemplateRef || null);
    setHasChanges(false);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplateId || !clinicId) return;
    
    setSaving(true);
    setError(null);
    try {
      // Save template name/description and patient ID requirements
      await updateCheckInTemplate(clinicId, selectedTemplateId, {
        name: templateName,
        description: templateDescription,
        requirePhotoID,
        requireInsuranceCard,
        conversationTemplateRef,
      });
      
      // Save clinic metadata (for CareMap integration, operating hours, etc.)
      await saveClinicMetadata();
      
      setSuccess('Clinic settings saved successfully');
      setHasChanges(false);
      await loadTemplates();
      
      // Clear success after 3 seconds (Leak-Proof: guard for unmount)
      setTimeout(() => { if (isMountedRef.current) setSuccess(null); }, 3000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error saving template:', err);
      setError('Failed to save clinic: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTemplate = async (name, departmentId) => {
    if (!clinicId || !user?.uid) return;
    
    setSaving(true);
    setError(null);
    try {
      // Get department to inherit specialty
      let inheritedSpecialties = [];
      if (departmentId) {
        const dept = departments.find(d => d.id === departmentId);
        if (dept?.specialty) {
          inheritedSpecialties = [dept.specialty];
        }
      }
      
      const templateId = await createCheckInTemplate(clinicId, {
        name: name || 'New Check-in Form',
        description: '',
        welcomeMessage: DEFAULT_WELCOME_MESSAGE,
        fields: [],
        departmentId: departmentId || null,
        specialties: inheritedSpecialties,
      }, user.uid);
      
      setSuccess('Clinic created successfully');
      setNewTemplateDialogOpen(false);
      await loadTemplates();
      
      // Select the new template
      const newTemplates = await getCheckInTemplates(clinicId);
      const newTemplate = newTemplates.find(t => t.id === templateId);
      if (newTemplate) {
        selectTemplate(newTemplate);
      }
      
      setTimeout(() => { if (isMountedRef.current) setSuccess(null); }, 3000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error creating template:', err);
      setError('Failed to create template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedTemplateId || !clinicId) return;
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    setSaving(true);
    setError(null);
    try {
      await deleteCheckInTemplate(clinicId, selectedTemplateId);
      setSuccess('Clinic deleted successfully');
      setTemplateMenuAnchor(null);
      await loadTemplates();
      
      setTimeout(() => { if (isMountedRef.current) setSuccess(null); }, 3000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error deleting template:', err);
      setError('Failed to delete template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selectedTemplateId || !clinicId) return;
    
    setSaving(true);
    setError(null);
    try {
      await setDefaultTemplate(clinicId, selectedTemplateId);
      setSuccess('Default template updated');
      setTemplateMenuAnchor(null);
      await loadTemplates();
      
      setTimeout(() => { if (isMountedRef.current) setSuccess(null); }, 3000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error setting default:', err);
      setError('Failed to set default template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateTemplate = async () => {
    if (!selectedTemplateId || !clinicId || !user?.uid) return;
    
    const currentTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!currentTemplate) return;
    
    setSaving(true);
    setError(null);
    try {
      await createCheckInTemplate(clinicId, {
        name: `${currentTemplate.name} (Copy)`,
        description: currentTemplate.description,
        welcomeMessage: currentTemplate.welcomeMessage || DEFAULT_WELCOME_MESSAGE,
        fields: currentTemplate.fields,
        isDefault: false,
      }, user.uid);
      
      setSuccess('Clinic duplicated successfully');
      setTemplateMenuAnchor(null);
      await loadTemplates();
      
      setTimeout(() => { if (isMountedRef.current) setSuccess(null); }, 3000);
    } catch (err) {
      console.error('[CheckInFormBuilder] Error duplicating template:', err);
      setError('Failed to duplicate template: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get current template
  const currentTemplate = templates.find(t => t.id === selectedTemplateId);
  const templateOwnerAdminId = currentTemplate?.encounterTemplateRef?.adminId || null;

  if (!clinicId) {
    return (
      <Alert severity="warning">
        Clinic ID not found. Please contact support to associate your account with a clinic.
      </Alert>
    );
  }

  const canEditMessagingConfig =
    userData?.role === 'facility' ||
    userData?.role === 'chartmind-supervisor' ||
    userData?.role === 'department-admin' ||
    userData?.role === 'chartmind-admin' ||
    userData?.role === 'chartmind-kijabe' ||
    userData?.role === 'f2';

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h2" sx={{ mt: { xs: 1, sm: 5 } }}>
            Clinic Manager
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure the data fields patients will see when checking in to your clinic
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: { xs: 1, sm: 5 } }}>
          {hasChanges && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
              onClick={handleSaveTemplate}
              disabled={saving}
            >
              Save Changes
            </Button>
          )}
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No clinics configured yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first clinic to start accepting patient check-ins
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewTemplateDialogOpen(true)}
          >
            Create First Clinic
          </Button>
        </Paper>
      ) : (
        <>
          {/* Clinic selector */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <Typography
                  component="label"
                  id="clinic-select-label"
                  htmlFor="clinic-select"
                  sx={{
                    display: 'block',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  <FieldHighlighter highlight={highlightClinicLabel} direction="ltr" duration={500} borderRadius="4px" padding="1em" followWithRtl>Clinic</FieldHighlighter>
                </Typography>
                <Select
                  value={selectedTemplateId || ''}
                  displayEmpty
                  inputProps={{ id: 'clinic-select', 'aria-labelledby': 'clinic-select-label' }}
                  onChange={(e) => {
                    if (e.target.value === '__create_new__') {
                      setNewTemplateDialogOpen(true);
                      return;
                    }
                    const template = templates.find(t => t.id === e.target.value);
                    if (template) selectTemplate(template);
                  }}
                >
                  <MenuItem value="" disabled sx={{ color: 'text.secondary' }}>
                    Select Clinic Here
                  </MenuItem>
                  <MenuItem value="__create_new__" sx={{ color: 'primary.main', fontWeight: 500 }}>
                    <Add fontSize="small" sx={{ mr: 1 }} />
                    Create New Clinic
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                  {departments.length > 0 ? (
                    // Group clinics by department
                    departments.map(dept => {
                      const deptClinics = templates.filter(t => t.departmentId === dept.id);
                      if (deptClinics.length === 0) return null;
                      return [
                        <ListSubheader key={`header-${dept.id}`} sx={{ bgcolor: 'grey.100', lineHeight: '32px' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Business fontSize="small" color="action" />
                            {dept.name}
                          </Box>
                        </ListSubheader>,
                        ...deptClinics.map(template => (
                          <MenuItem key={template.id} value={template.id} sx={{ pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LocalHospital fontSize="small" color="primary" />
                              {template.name}
                              {template.isDefault && (
                                <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                              )}
                            </Box>
                          </MenuItem>
                        ))
                      ];
                    })
                  ) : (
                    // No departments yet - show flat list
                    templates.map(template => (
                      <MenuItem key={template.id} value={template.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalHospital fontSize="small" color="primary" />
                          {template.name}
                          {template.isDefault && (
                            <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  )}
                  {/* Show clinics without department at the end */}
                  {departments.length > 0 && templates.some(t => !t.departmentId) && [
                    <ListSubheader key="header-unassigned" sx={{ bgcolor: 'warning.50', lineHeight: '32px' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning fontSize="small" color="warning" />
                        Unassigned
                      </Box>
                    </ListSubheader>,
                    ...templates.filter(t => !t.departmentId).map(template => (
                      <MenuItem key={template.id} value={template.id} sx={{ pl: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalHospital fontSize="small" color="warning" />
                          {template.name}
                          {template.isDefault && (
                            <Chip label="Default" size="small" color="primary" sx={{ height: 20 }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))
                  ]}
                </Select>
              </FormControl>

              {selectedTemplateId && (
                <>
                  <TextField
                    size="small"
                    label="Clinic Name"
                    value={templateName}
                    onChange={(e) => {
                      setTemplateName(e.target.value);
                      setHasChanges(true);
                    }}
                    sx={{ minWidth: 200 }}
                  />

                  <TextField
                    size="small"
                    label="Description"
                    value={templateDescription}
                    onChange={(e) => {
                      setTemplateDescription(e.target.value);
                      setHasChanges(true);
                    }}
                    sx={{ flex: 1, minWidth: 200 }}
                  />

                  <IconButton
                    onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
                  >
                    <MoreVert />
                  </IconButton>

                  <Menu
                    anchorEl={templateMenuAnchor}
                    open={Boolean(templateMenuAnchor)}
                    onClose={() => setTemplateMenuAnchor(null)}
                  >
                    <MenuItem onClick={handleSetDefault} disabled={currentTemplate?.isDefault}>
                      <Star fontSize="small" sx={{ mr: 1 }} />
                      Set as Default Clinic
                    </MenuItem>
                    <MenuItem onClick={handleDuplicateTemplate}>
                      <ContentCopy fontSize="small" sx={{ mr: 1 }} />
                      Duplicate Clinic
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleDeleteTemplate} sx={{ color: 'error.main' }}>
                      <Delete fontSize="small" sx={{ mr: 1 }} />
                      Delete Clinic
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Paper>

          {/* Prompt to select a clinic when none is selected */}
          {!selectedTemplateId && (
            <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
              <LocalHospital sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a clinic to get started
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a clinic from the dropdown above to view and manage its check-in QR code, form, and operating hours. Then set this clinic's hours in <strong>Clinic Settings</strong> below.
              </Typography>
            </Paper>
          )}

          {/* Check-in QR, form, and Clinic Settings – only when a clinic is selected */}
          {clinicId && templates.length > 0 && (
            <>
          {selectedTemplateId && (<>
          <Paper sx={{ mt: 3, mb: 3, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
              {/* QR Code */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <QrCode2 color="primary" />
                  Patient Check-in QR Code
                </Typography>
                
                {joinUrl ? (
                  <Box
                    sx={{
                      display: 'inline-block',
                      p: 2,
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      bgcolor: 'white',
                    }}
                  >
                    <QRCodeSVG
                      value={joinUrl}
                      size={180}
                      level="H"
                      includeMargin
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: 180,
                      height: 180,
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                    }}
                  >
                    <QrCode2 sx={{ fontSize: 64, color: 'grey.400' }} />
                  </Box>
                )}
              </Box>

              {/* Link and Actions */}
              <Box sx={{ flex: 1 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  The check-in link and QR code come from a Patient Encounter Template (PET) linked to a check-in template. Creating a PET automatically generates an invite code; a check-in template is only created and linked when a ChartMind Admin uses &quot;Create & Link&quot; from the Query Registry with a clinic selected. If you don&apos;t see a link here, ask a ChartMind Admin to create and link a PET for this clinic.
                </Alert>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Patients scan this QR code or visit the link below to check in.
                  Display this code at your clinic's check-in area.
                </Typography>

                {joinUrl ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Check-in URL
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      <Link fontSize="small" color="action" />
                      <Box sx={{ flex: 1 }}>{joinUrl}</Box>
                      <Tooltip title={linkCopied ? 'Copied!' : 'Copy link'}>
                        <IconButton 
                          size="small" 
                          onClick={handleCopyLink}
                          color={linkCopied ? 'success' : 'default'}
                        >
                          {linkCopied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="info">
                    <Typography variant="body2">
                      This clinic does not yet have a Patient Encounter Template (PET) created and linked to it.
                      There is one check-in template per PET; the PET provides the invite code used for the QR code and check-in URL.
                      A ChartMind Admin can create a new PET for this clinic (or link an existing one) from the{' '}
                      <strong>Query Registry</strong> (Open Query Registry below).
                    </Typography>
                  </Alert>
                )}

                {/* Display on CareMap Toggle - within QR section for visibility distribution */}
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mt: 3, 
                    p: 2, 
                    bgcolor: displayOnCareMap ? 'success.50' : 'grey.50',
                    borderColor: displayOnCareMap ? 'success.main' : 'grey.300',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={displayOnCareMap}
                        onChange={(e) => {
                          setDisplayOnCareMap(e.target.checked);
                          setHasChanges(true);
                        }}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MapIcon fontSize="small" color={displayOnCareMap ? 'success' : 'action'} />
                          <Typography variant="body1" fontWeight={500}>
                            Display check-in link on CareMap
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          When enabled, patients can find and use this check-in link directly from 
                          your facility's CareMap entry. Requires clinic name and at least one specialty in Clinic Settings.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
                  />
                  
                  {displayOnCareMap && (!templateName || clinicSpecialties.length === 0) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      Please set a clinic name and select at least one specialty in <strong>Clinic Settings</strong> below to display on CareMap.
                    </Alert>
                  )}
                  
                  {displayOnCareMap && templateName && clinicSpecialties.length > 0 && (
                    <Alert severity="success" sx={{ mt: 2 }} icon={<MapIcon />}>
                      Your clinic will appear on CareMap with check-in enabled. Patients searching for 
                      {' '}<strong>{clinicSpecialties.map(s => getSpecialtyLabel(s)).join(', ')}</strong>{' '}
                      will see your facility.
                    </Alert>
                  )}
                </Paper>
              </Box>
            </Box>
          </Paper>

          {/* Patient Identification Section */}
          <Paper sx={{ mt: 3, mb: 3, p: 3 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Badge color="primary" />
              Patient Identification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure what identification documents patients must provide during check-in.
              Captured images and extracted data will be available to providers in ChartMind.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: requirePhotoID ? 'info.50' : 'grey.50', borderColor: requirePhotoID ? 'info.main' : 'grey.300' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requirePhotoID}
                    onChange={(e) => { setRequirePhotoID(e.target.checked); setHasChanges(true); }}
                    color="info"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge fontSize="small" color={requirePhotoID ? 'info' : 'action'} />
                      <Typography variant="body1" fontWeight={500}>
                        Require Photo ID
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Patients must photograph their government-issued ID during check-in.
                      Name and date of birth are extracted automatically and pre-fill the check-in form.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, bgcolor: requireInsuranceCard ? 'info.50' : 'grey.50', borderColor: requireInsuranceCard ? 'info.main' : 'grey.300' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={requireInsuranceCard}
                    onChange={(e) => { setRequireInsuranceCard(e.target.checked); setHasChanges(true); }}
                    color="info"
                  />
                }
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CreditCard fontSize="small" color={requireInsuranceCard ? 'info' : 'action'} />
                      <Typography variant="body1" fontWeight={500}>
                        Require Insurance Card
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Patients must photograph their insurance card during check-in.
                      Plan name, member ID, and group number are extracted automatically.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0, width: '100%' }}
              />
            </Paper>
            </Paper>

          {/* Clinic Settings – identity, specialties, operating hours for the selected clinic */}
          <Accordion
            defaultExpanded 
            sx={{ 
              mb: 3,
              '&:before': { display: 'none' },
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ bgcolor: 'secondary.50' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="secondary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Clinic Settings
                </Typography>
                <Chip 
                  label="Identity & Hours" 
                  size="small" 
                  color="secondary"
                  variant="outlined"
                  sx={{ ml: 1, height: 22 }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure your clinic's identity, specialties, and operating hours. This information 
                is displayed on CareMap and used to validate check-in availability.
              </Typography>

              {/* Clinic Name - now unified with the name in the header */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  mb: 3, 
                  bgcolor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <LocalHospital color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Clinic Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {templateName || clinicDisplayName || 'Not set'}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Edit in header above
                </Typography>
              </Paper>

              {/* Specialties Multi-Select */}
              <Autocomplete
                multiple
                options={AAMC_SPECIALTIES}
                value={AAMC_SPECIALTIES.filter(s => clinicSpecialties.includes(s.value))}
                onChange={(e, newValue) => {
                  setClinicSpecialties(newValue.map(s => s.value));
                  setHasChanges(true);
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.label}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.value}
                      label={option.label}
                      size="small"
                      color="secondary"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Specialties"
                    placeholder={clinicSpecialties.length === 0 ? "Select one or more specialties..." : ""}
                    helperText="Select the medical specialties this clinic serves. Patients can filter CareMap by specialty."
                  />
                )}
                sx={{ mb: 3 }}
              />

              {/* Common Specialties Quick Add */}
              {clinicSpecialties.length === 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Quick add common specialties:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {COMMON_CLINIC_SPECIALTIES.slice(0, 8).map(specialty => (
                      <Chip
                        key={specialty}
                        label={getSpecialtyLabel(specialty)}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setClinicSpecialties([...clinicSpecialties, specialty]);
                          setHasChanges(true);
                        }}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Operating Hours Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccessTime color="secondary" />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Operating Hours
                  </Typography>
                </Box>

                {/* 24/7 and By Appointment Only toggles */}
                <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={is24x7}
                        onChange={(e) => {
                          setIs24x7(e.target.checked);
                          setHasChanges(true);
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AllInclusive fontSize="small" />
                        <Typography variant="body2">Open 24/7/365</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={byAppointmentOnly}
                        onChange={(e) => {
                          setByAppointmentOnly(e.target.checked);
                          setHasChanges(true);
                        }}
                        color="secondary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule fontSize="small" />
                        <Typography variant="body2">By Appointment Only</Typography>
                      </Box>
                    }
                  />
                </Box>

                {/* Weekly Schedule (hidden if 24/7) */}
                {!is24x7 && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Weekly Schedule
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => {
                          // Copy Monday's hours to all weekdays
                          const mondayHours = weeklyHours.monday;
                          setWeeklyHours(prev => ({
                            ...prev,
                            tuesday: { ...mondayHours },
                            wednesday: { ...mondayHours },
                            thursday: { ...mondayHours },
                            friday: { ...mondayHours },
                          }));
                          setHasChanges(true);
                        }}
                      >
                        Copy Mon → Weekdays
                      </Button>
                    </Box>
                    
                    {Object.entries(weeklyHours).map(([day, hours]) => (
                      <Box 
                        key={day} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2, 
                          mb: 1.5,
                          flexWrap: 'wrap',
                          py: 1,
                          borderBottom: day !== 'sunday' ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={hours.isOpen}
                              onChange={(e) => {
                                setWeeklyHours(prev => ({
                                  ...prev,
                                  [day]: { ...prev[day], isOpen: e.target.checked }
                                }));
                                setHasChanges(true);
                              }}
                              size="small"
                            />
                          }
                          label={
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                width: 80, 
                                textTransform: 'capitalize',
                                fontWeight: hours.isOpen ? 500 : 400,
                                color: hours.isOpen ? 'text.primary' : 'text.disabled',
                              }}
                            >
                              {day}
                            </Typography>
                          }
                          sx={{ mr: 2, minWidth: 130 }}
                        />
                        
                        {hours.isOpen ? (
                          <>
                            <TextField
                              type="time"
                              size="small"
                              label="Open"
                              value={hours.openTime}
                              onChange={(e) => {
                                setWeeklyHours(prev => ({
                                  ...prev,
                                  [day]: { ...prev[day], openTime: e.target.value }
                                }));
                                setHasChanges(true);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 120 }}
                            />
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <TextField
                              type="time"
                              size="small"
                              label="Close"
                              value={hours.closeTime}
                              onChange={(e) => {
                                setWeeklyHours(prev => ({
                                  ...prev,
                                  [day]: { ...prev[day], closeTime: e.target.value }
                                }));
                                setHasChanges(true);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 120 }}
                            />
                            
                            {/* Break time */}
                            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                            <TextField
                              type="time"
                              size="small"
                              label="Break Start"
                              value={hours.breakStart || ''}
                              onChange={(e) => {
                                setWeeklyHours(prev => ({
                                  ...prev,
                                  [day]: { ...prev[day], breakStart: e.target.value }
                                }));
                                setHasChanges(true);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 120 }}
                              placeholder="Optional"
                            />
                            <Typography variant="body2" color="text.secondary">to</Typography>
                            <TextField
                              type="time"
                              size="small"
                              label="Break End"
                              value={hours.breakEnd || ''}
                              onChange={(e) => {
                                setWeeklyHours(prev => ({
                                  ...prev,
                                  [day]: { ...prev[day], breakEnd: e.target.value }
                                }));
                                setHasChanges(true);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 120 }}
                              placeholder="Optional"
                            />
                            
                            {/* Session Capacity - only shown when By Appointment Only is enabled */}
                            {byAppointmentOnly && (
                              <>
                                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                                {hours.breakStart && hours.breakEnd ? (
                                  // Has break - show morning and afternoon capacity
                                  <>
                                    <TextField
                                      type="number"
                                      size="small"
                                      label="AM Patients"
                                      value={hours.morningCapacity || ''}
                                      onChange={(e) => {
                                        setWeeklyHours(prev => ({
                                          ...prev,
                                          [day]: { ...prev[day], morningCapacity: parseInt(e.target.value) || 0 }
                                        }));
                                        setHasChanges(true);
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ min: 0, max: 100 }}
                                      sx={{ width: 90 }}
                                    />
                                    <TextField
                                      type="number"
                                      size="small"
                                      label="PM Patients"
                                      value={hours.afternoonCapacity || ''}
                                      onChange={(e) => {
                                        setWeeklyHours(prev => ({
                                          ...prev,
                                          [day]: { ...prev[day], afternoonCapacity: parseInt(e.target.value) || 0 }
                                        }));
                                        setHasChanges(true);
                                      }}
                                      InputLabelProps={{ shrink: true }}
                                      inputProps={{ min: 0, max: 100 }}
                                      sx={{ width: 90 }}
                                    />
                                  </>
                                ) : (
                                  // No break - show single daily capacity
                                  <TextField
                                    type="number"
                                    size="small"
                                    label="Daily Patients"
                                    value={hours.morningCapacity || ''}
                                    onChange={(e) => {
                                      const capacity = parseInt(e.target.value) || 0;
                                      setWeeklyHours(prev => ({
                                        ...prev,
                                        [day]: { 
                                          ...prev[day], 
                                          morningCapacity: capacity,
                                          afternoonCapacity: 0 // Set to 0 when no break
                                        }
                                      }));
                                      setHasChanges(true);
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: 0, max: 100 }}
                                    sx={{ width: 110 }}
                                  />
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                            Closed
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Paper>
                )}
                
                {/* By Appointment Only Info */}
                {byAppointmentOnly && !is24x7 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>By Appointment Only:</strong> Set the maximum number of patients for each session above. 
                      {' '}When a session reaches capacity, check-ins will be blocked with a message showing the next available time.
                      {' '}Days with a break time have separate morning (AM) and afternoon (PM) capacities.
                    </Typography>
                  </Alert>
                )}

                {/* Closure Exceptions */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EventBusy color="warning" fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      Scheduled Closures
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Holidays, maintenance, etc. - check-ins blocked on these dates)
                    </Typography>
                  </Box>
                  
                  {/* Add new closure */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-end' }}>
                    <TextField
                      type="date"
                      size="small"
                      label="Date"
                      value={newClosureDate}
                      onChange={(e) => setNewClosureDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 160 }}
                    />
                    <TextField
                      size="small"
                      label="Reason (optional)"
                      value={newClosureReason}
                      onChange={(e) => setNewClosureReason(e.target.value)}
                      placeholder="e.g., Holiday, Staff Training"
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => {
                        if (newClosureDate) {
                          setClosureExceptions(prev => [
                            ...prev,
                            { date: newClosureDate, reason: newClosureReason || 'Closed' }
                          ].sort((a, b) => a.date.localeCompare(b.date)));
                          setNewClosureDate('');
                          setNewClosureReason('');
                          setHasChanges(true);
                        }
                      }}
                      disabled={!newClosureDate}
                    >
                      Add
                    </Button>
                  </Box>
                  
                  {/* List of closures */}
                  {closureExceptions.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {closureExceptions.map((closure, idx) => (
                        <Chip
                          key={idx}
                          label={`${new Date(closure.date + 'T00:00:00').toLocaleDateString()} - ${closure.reason}`}
                          onDelete={() => {
                            setClosureExceptions(prev => prev.filter((_, i) => i !== idx));
                            setHasChanges(true);
                          }}
                          color="warning"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No scheduled closures
                    </Typography>
                  )}
                </Paper>

                <Button
                  variant="contained"
                  sx={{ mt: 3 }}
                  onClick={handleSaveClinicSettingsOnly}
                  disabled={saving || clinicMetadataLoading}
                  startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {saving ? 'Saving…' : 'Save clinic settings'}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Fee configuration (registration/check-in fee) — facility and chartmind-supervisor */}
          <Accordion
            sx={{
              mb: 3,
              '&:before': { display: 'none' },
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: 'secondary.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CreditCard color="secondary" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Fee configuration
                </Typography>
                <Chip label="Check-in fee" size="small" color="secondary" variant="outlined" sx={{ ml: 1, height: 22 }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set the registration (check-in) fee collected per visit. You can collect via card (Stripe) or M-Pesa in Kenya (Paystack).
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={feeConfig.registration?.enabled ?? false}
                      onChange={(e) => {
                        setFeeConfig(prev => ({
                          ...prev,
                          registration: { ...prev.registration, enabled: e.target.checked },
                        }));
                        setHasChanges(true);
                      }}
                    />
                  }
                  label="Enable registration fee"
                />
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Amount (KSh)"
                  value={feeConfig.registration?.amountKsh ?? 0}
                  onChange={(e) => {
                    const v = Math.max(0, parseInt(e.target.value, 10) || 0);
                    setFeeConfig(prev => ({
                      ...prev,
                      registration: { ...prev.registration, amountKsh: v },
                    }));
                    setHasChanges(true);
                  }}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ mt: 2, maxWidth: 200 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Label"
                  value={feeConfig.registration?.label || 'Registration fee'}
                  onChange={(e) => {
                    setFeeConfig(prev => ({
                      ...prev,
                      registration: { ...prev.registration, label: e.target.value || 'Registration fee' },
                    }));
                    setHasChanges(true);
                  }}
                  sx={{ mt: 2, maxWidth: 280 }}
                />
                <FormControl fullWidth sx={{ mt: 2, maxWidth: 280 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={feeConfig.registration?.provider || 'paystack'}
                    label="Provider"
                    onChange={(e) => {
                      setFeeConfig(prev => ({
                        ...prev,
                        registration: { ...prev.registration, provider: e.target.value },
                      }));
                      setHasChanges(true);
                    }}
                  >
                    <MenuItem value="stripe">Stripe (card)</MenuItem>
                    <MenuItem value="paystack">Paystack (M-Pesa / Kenya)</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
              <Button
                variant="contained"
                sx={{ mt: 1 }}
                onClick={handleSaveClinicSettingsOnly}
                disabled={saving || clinicMetadataLoading}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {saving ? 'Saving…' : 'Save clinic settings'}
              </Button>
            </AccordionDetails>
          </Accordion>

          {/* PET (Patient Encounter Template) management link */}
          <Paper
            variant="outlined"
            sx={{
              p: 3, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.200',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Form Fields, Welcome Message &amp; Intake Conversation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check-in form fields, welcome message, and AI intake conversations are managed
                from the LLM Query Registry alongside your ChartMind prompts.
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/dashboard/llm-manager/queries')}
              sx={{ ml: 2, flexShrink: 0 }}
            >
              Open Query Registry
            </Button>
          </Paper>

          {/* Conversation template assignment for SecureLink check-in */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              SecureLink conversation template
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick the conversation template used during patient check-in for this clinic.
            </Typography>
            <ConversationTemplatePicker
              value={conversationTemplateRef}
              onChange={(nextValue) => {
                setConversationTemplateRef(nextValue);
                setHasChanges(true);
              }}
              touchpoint="securelink"
              adminId={templateOwnerAdminId}
              helperText={
                templateOwnerAdminId
                  ? 'Changes are saved with this clinic template.'
                  : 'Link a Patient Encounter Template first to enable custom template choices.'
              }
              disabled={!templateOwnerAdminId}
            />
          </Paper>
          </>)}
        </>
      )}

      {/* New Clinic Dialog */}
      <Dialog
        open={newTemplateDialogOpen}
        onClose={() => setNewTemplateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Clinic</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Each clinic has its own check-in form and patient queue.
          </Typography>
          
          {/* Department Selection */}
          {departments.length > 0 && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={selectedDepartmentForNew}
                label="Department"
                onChange={(e) => setSelectedDepartmentForNew(e.target.value)}
              >
                {departments.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business fontSize="small" color="action" />
                      {dept.name}
                      {dept.specialty && (
                        <Chip 
                          label={getSpecialtyLabel(dept.specialty)} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, ml: 1 }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {(() => {
                const selectedDept = departments.find(d => d.id === selectedDepartmentForNew);
                if (selectedDept?.specialty) {
                  return (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      This clinic will inherit the {getSpecialtyLabel(selectedDept.specialty)} specialty from the department.
                    </Typography>
                  );
                }
                return null;
              })()}
            </FormControl>
          )}
          
          {departments.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No departments have been created yet. This clinic will be unassigned.
              <br />
              <Typography variant="caption">
                Facility admins can create departments in Department Management.
              </Typography>
            </Alert>
          )}
          
          <TextField
            inputRef={newTemplateNameRef}
            autoFocus
            margin="dense"
            label="Clinic Name"
            fullWidth
            variant="outlined"
            placeholder="e.g., Cardiology Clinic, Family Medicine"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTemplateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const value = newTemplateNameRef.current?.value;
              handleCreateTemplate(value?.trim() || 'New Clinic', selectedDepartmentForNew);
            }}
          >
            Create Clinic
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Field Dialog, Conversation Template Browser, and Conversation Template Edit
           dialogs have been moved to PETManagementModal in the Query Registry. */}

      {/* Provider access (ChartMind) - Phase 3 */}
      <ProviderApprovalSettings clinicId={clinicId} />

      {/* Designate ChartMind Admin per clinic (plan: hierarchical join link system) */}
      {(userData?.role === 'chartmind-supervisor' || userData?.role === 'department-admin' || userData?.role === 'facility') && clinicId && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            ChartMind Admin for this clinic
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Invite or assign a ChartMind Admin for this clinic. Use Credential Management to send an invitation or assign an existing user.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<PersonAdd />}
            onClick={() => navigate('/dashboard/credential-management')}
          >
            Invite or assign ChartMind Admin
          </Button>
        </Paper>
      )}

      {/* Registrar config (subset) - Phase 4: supervisor and clinic-registrar */}
      {(userData?.role === 'chartmind-supervisor' || userData?.role === 'clinic-registrar') && (
        <RegistrarConfigCard clinicId={clinicId} />
      )}

      {/* Registrar assignment - Phase 4: facility/admin write; supervisor read-only */}
      {(userData?.role === 'facility' || userData?.role === 'chartmind-admin' || userData?.role === 'chartmind-kijabe' || userData?.role === 'chartmind-supervisor') && (
        <RegistrarAssignmentCard
          clinicId={clinicId}
          currentUserId={user?.uid}
          canWrite={userData?.role === 'facility' || userData?.role === 'chartmind-admin' || userData?.role === 'chartmind-kijabe'}
        />
      )}

      {clinicId && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            Messaging
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure facility-level messaging defaults for patient messaging campaigns.
          </Typography>

          {messagingConfigLoading ? (
            <CircularProgress size={18} />
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                label="Reply email domain"
                value={messagingConfig.replyEmailDomain}
                onChange={(event) =>
                  setMessagingConfig((prev) => ({
                    ...prev,
                    replyEmailDomain: event.target.value,
                  }))
                }
                helperText="Example: yourclinic.org (or @yourclinic.org)"
                disabled={!canEditMessagingConfig || messagingConfigSaving}
              />
              <TextField
                label="Max recipients per message send"
                type="number"
                value={messagingConfig.maxRecipientsPerBlast}
                onChange={(event) =>
                  setMessagingConfig((prev) => ({
                    ...prev,
                    maxRecipientsPerBlast: event.target.value,
                  }))
                }
                helperText="Default recommendation: 500"
                inputProps={{ min: 1 }}
                disabled={!canEditMessagingConfig || messagingConfigSaving}
              />
              {messagingConfigError ? <Alert severity="error">{messagingConfigError}</Alert> : null}
              {messagingConfigSuccess ? <Alert severity="success">{messagingConfigSuccess}</Alert> : null}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={handleSaveMessagingConfig}
                  disabled={!canEditMessagingConfig || messagingConfigSaving}
                  startIcon={<Save />}
                >
                  {messagingConfigSaving ? 'Saving…' : 'Save messaging settings'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </>
    )}

    </Box>
  );
};

export default CheckInFormBuilder;
